import type {
  ItineraryUpdate,
  EnrichmentCompleteEvent,
  StreamingStopVisit,
  TripChatItem,
  TripStopWithDays,
  TripItineraryData,
} from 'tripPlanner/api/types'
import { sortBy } from 'lib/array/arrayUtils'

const TRANSPORT_ITEM_TYPES: ReadonlySet<TripChatItem['type']> = new Set([
  'FLIGHT', 'TRAIN', 'BUS', 'FERRY', 'OTHER_TRANSPORT',
])

function resolveStopVisitDayRange(
  stop: TripStopWithDays,
  stopVisit: StreamingStopVisit,
): { startDayNumber: number; endDayNumber: number } | undefined {
  if (
    stopVisit.startDayNumber != null &&
    stopVisit.endDayNumber != null
  ) {
    return {
      startDayNumber: stopVisit.startDayNumber,
      endDayNumber: stopVisit.endDayNumber,
    }
  }

  if (stopVisit.startDayId == null && stopVisit.endDayId == null) {
    return undefined
  }

  const startDay = stop.days.find(day => day.id === stopVisit.startDayId)
  const endDay = stop.days.find(day =>
    day.id === (stopVisit.endDayId ?? stopVisit.startDayId),
  )

  if (!startDay || !endDay) {
    return undefined
  }

  return {
    startDayNumber: Math.min(startDay.dayNumber, endDay.dayNumber),
    endDayNumber: Math.max(startDay.dayNumber, endDay.dayNumber),
  }
}

/**
 * Remove an item by ID from all locations (days + itemsWithoutDay) across all stops.
 * Used by item_updated (to clear old positions before re-placing) and item_deleted.
 */
function removeItemFromAllLocations(
  stops: Array<TripStopWithDays>,
  itemId: number,
): Array<TripStopWithDays> {
  return stops.map(stop => ({
    ...stop,
    days: stop.days.map(day => ({
      ...day,
      items: day.items.filter(i => i.id !== itemId),
    })),
    itemsWithoutDay: stop.itemsWithoutDay.filter(i => i.id !== itemId),
  }))
}

/**
 * Place an item into the correct stops and days based on stop visits.
 *
 * For each stop visit:
 * - If startDayNumber/endDayNumber are present, add the item to every day in that range
 *   with computed day-specific fields (role, isCheckIn, isCheckOut, dayOfStay, totalDays).
 * - If days in the range don't exist in the cache yet, park the item in itemsWithoutDay.
 * - If no day numbers, the item is unassigned — add to itemsWithoutDay.
 */
function placeItemByStopVisits(
  stops: Array<TripStopWithDays>,
  itemId: number,
  item: TripChatItem,
  stopVisits: Array<StreamingStopVisit>,
): Array<TripStopWithDays> {
  let result = stops

  for (const sv of stopVisits) {
    result = result.map(stop => {
      if (stop.id !== sv.tripStopId) return stop

      const dayRange = resolveStopVisitDayRange(stop, sv)
      const startDay = dayRange?.startDayNumber
      const endDay = dayRange?.endDayNumber

      if (startDay == null || endDay == null) {
        if (!stop.itemsWithoutDay.some(i => i.id === itemId)) {
          return { ...stop, itemsWithoutDay: [...stop.itemsWithoutDay, item] }
        }
        return stop
      }

      const totalDays = endDay - startDay + 1

      const hasMatchingDay = stop.days.some(d =>
        d.dayNumber >= startDay && d.dayNumber <= endDay,
      )

      if (!hasMatchingDay) {
        if (!stop.itemsWithoutDay.some(i => i.id === itemId)) {
          return { ...stop, itemsWithoutDay: [...stop.itemsWithoutDay, item] }
        }
        return stop
      }

      return {
        ...stop,
        days: stop.days.map(day => {
          if (
            day.dayNumber >= startDay &&
            day.dayNumber <= endDay &&
            !day.items.some(i => i.id === itemId)
          ) {
            const dayItem = {
              ...item,
              role: sv.role,
              isCheckIn: day.dayNumber === startDay,
              isCheckOut: day.dayNumber === endDay,
              dayOfStay: day.dayNumber - startDay + 1,
              totalDays,
            }
            return { ...day, items: [...day.items, dayItem] }
          }
          return day
        }),
      }
    })
  }

  return result
}

/**
 * Apply a batch of itinerary updates to the current state.
 * Returns a new itinerary data object (immutable update).
 */
export function applyItineraryUpdates(
  current: TripItineraryData | undefined,
  updates: Array<ItineraryUpdate>,
): TripItineraryData {
  const base: TripItineraryData = current ?? { tripChatId: 0, stops: [] }
  const updated: TripItineraryData = { ...base, stops: [...base.stops] }

  for (const update of updates) {
    if (update.kind === 'stop_added') {
      if (!updated.stops.some(s => s.id === update.stopId)) {
        const insertIndex = updated.stops.findIndex(s => s.stopSeq > update.stop.stopSeq)
        if (insertIndex === -1) {
          updated.stops.push(update.stop)
        } else {
          updated.stops.splice(insertIndex, 0, update.stop)
        }
      }
    } else if (update.kind === 'stop_updated') {
      updated.stops = updated.stops.map(stop =>
        stop.id === update.stopId ? update.stop : stop,
      )
    } else if (update.kind === 'day_added') {
      updated.stops = updated.stops.map(stop =>
        stop.id === update.stopId && !stop.days.some(d => d.id === update.dayId)
          ? { ...stop, days: [...stop.days, update.day] }
          : stop,
      )
    } else if (update.kind === 'item_added') {
      if (update.stopVisits?.length > 0 && !TRANSPORT_ITEM_TYPES.has(update.item.type)) {
        updated.stops = placeItemByStopVisits(
          updated.stops, update.itemId, update.item, update.stopVisits,
        )
      }
    } else if (update.kind === 'item_updated') {
      if (TRANSPORT_ITEM_TYPES.has(update.item.type)) {
        updated.stops = removeItemFromAllLocations(updated.stops, update.itemId)
      } else if (update.stopVisits?.length > 0) {
        const hasKnownStop = update.stopVisits.some(sv =>
          updated.stops.some(stop => stop.id === sv.tripStopId),
        )
        if (hasKnownStop) {
          updated.stops = placeItemByStopVisits(
            removeItemFromAllLocations(updated.stops, update.itemId),
            update.itemId, update.item, update.stopVisits,
          )
        }
      }
    } else if (update.kind === 'stop_deleted') {
      updated.stops = updated.stops.filter(stop => stop.id !== update.stopId)
    } else if (update.kind === 'day_deleted') {
      updated.stops = updated.stops.map(stop =>
        stop.id === update.stopId
          ? { ...stop, days: stop.days.filter(day => day.id !== update.dayId) }
          : stop,
      )
    } else if (update.kind === 'item_deleted') {
      updated.stops = removeItemFromAllLocations(updated.stops, update.itemId)
    }
  }

  updated.stops = sortBy(updated.stops, stop => stop.stopSeq, 'asc')

  return updated
}

/**
 * Apply enrichment data to an item in the itinerary.
 * Returns a new itinerary data object (immutable update).
 */
export function applyEnrichmentUpdate(
  current: TripItineraryData | undefined,
  event: EnrichmentCompleteEvent,
): TripItineraryData | undefined {
  if (!current) return current

  if (event.entityType === 'stop') {
    return {
      ...current,
      stops: current.stops.map(stop =>
        stop.id === event.stopId
          ? {
              ...stop,
              enrichmentStatus: 'enriched' as const,
              imageIds: event.imageIds,
              ...(event.placeDetail && { stopPlaceDetail: event.placeDetail }),
            }
          : stop,
      ),
    }
  }

  if (event.entityType === 'item') {
    return {
      ...current,
      stops: current.stops.map(stop => ({
        ...stop,
        days: stop.days.map(day => ({
          ...day,
          items: day.items.map(item =>
            item.id === event.itemId
              ? { ...item, enrichmentStatus: 'enriched' as const, ...(event.placeDetail && { itemPlaceDetail: event.placeDetail }) }
              : item,
          ),
        })),
        itemsWithoutDay: stop.itemsWithoutDay.map(item =>
          item.id === event.itemId
            ? { ...item, enrichmentStatus: 'enriched' as const, ...(event.placeDetail && { itemPlaceDetail: event.placeDetail }) }
            : item,
        ),
      })),
    }
  }

  return current
}
